import React, { useState, memo, useEffect, useMemo } from "react";
import { Input, Tree, theme } from "antd";
import * as Icon from "@ant-design/icons";
import _ from "lodash";
import { useAppContext } from "context/app";
import { findParentKeys, toTreeData } from "common/folder_common";

const { useToken } = theme;

function FilterFolder({ value, onChange }) {
    const [initialValueChecked, setInitialValueChecked] = useState(false);
    const [expandedKeys, setExpandedKeys] = useState([]);
    const [searchValue, setSearchValue] = useState("");
    const { token } = useToken();
    const folders = useAppContext().folderList;
    const treeData = toTreeData(folders);

    useEffect(() => {
        if (folders.length > 0) {
            setExpandedKeys([folders[0].id]);
        }
    }, [folders]);

    useEffect(() => {
        if (value?.length > 0 && !initialValueChecked) {
            const checkedSet = new Set();

            value.forEach((folderId) => {
                const parentKeyArray = findParentKeys(treeData, folderId);
                parentKeyArray.forEach((key) => {
                    checkedSet.add(key);
                });
            });

            setExpandedKeys(Array.from(checkedSet));
            setInitialValueChecked(true);
        }
    }, [value]);

    const onExpand = (keys) => {
        setExpandedKeys(keys);
    };

    const handleSearch = (e) => {
        const { value } = e.target;
        const newExpandedKeys = folders
            .map((item) => {
                if (item.name && item.name.toLowerCase().indexOf(value.toLowerCase()) > -1) {
                    return findParentKeys(treeData, item.id);
                }
                return [];
            })
            .flat()
            .filter((key, index, self) => self.indexOf(key) === index);

        setExpandedKeys(newExpandedKeys);
        setSearchValue(value);
    };

    const onCheck = (checkedKeysValue) => {
        console.log("onCheck", checkedKeysValue);
        onChange(checkedKeysValue.checked);
    };

    const onSelect = (selectedKeysValue, info) => {
        console.log("onSelect", info);
        const selectedKey = info.node.key;
        const checkedKeysValue = value.includes(selectedKey) ? value.filter((key) => key !== selectedKey) : [...value, selectedKey];
        onChange(checkedKeysValue);
    };

    const data = useMemo(() => {
        if (searchValue === "") {
            setExpandedKeys([folders[0].id]);
        }

        const normalizeString = (str) => str.toLowerCase();
        const loop = (data) =>
            data.map((item) => {
                const strTitle = item.title;
                const normalizedTitle = normalizeString(strTitle);
                const normalizedSearchValue = normalizeString(searchValue);

                const index = normalizedTitle.indexOf(normalizedSearchValue);
                const beforeStr = strTitle.substring(0, index);
                const highlightedStr = strTitle.substring(index, index + searchValue.length);
                const afterStr = strTitle.slice(index + searchValue.length);
                const title =
                    index > -1 && searchValue ? (
                        <span key={item.key}>
                            {beforeStr}
                            <span style={{ color: token.colorPrimary }}>{highlightedStr}</span>
                            {afterStr}
                        </span>
                    ) : (
                        <span key={item.key} className="tree-filter-title">
                            {strTitle}
                        </span>
                    );

                if (item.children) {
                    return {
                        id: item.id,
                        value: item.id,
                        title,
                        key: item.key,
                        children: loop(item.children),
                    };
                }

                return {
                    title,
                    key: item.key,
                    id: item.id,
                    value: item.id,
                };
            });

        return loop(treeData);
    }, [searchValue]);

    return (
        <div>
            <div>
                <Input style={{ margin: "10px 0 12px" }} placeholder="Search" addonBefore={<Icon.SearchOutlined />} onChange={handleSearch} />
            </div>
            <div style={{ backgroundColor: token.colorBgContainer }}>
                <Tree
                    checkable
                    checkStrictly={true}
                    style={{
                        width: "100%",
                    }}
                    suffixIcon={<Icon.DownOutlined size={64} />}
                    treeData={data}
                    placeholder="Please select"
                    showLine={true}
                    expandedKeys={expandedKeys}
                    onExpand={onExpand}
                    onSelect={onSelect}
                    onCheck={onCheck}
                    checkedKeys={value}
                    value={value}
                />
            </div>
        </div>
    );
}

export default memo(FilterFolder);
